import {computed, inject, onBeforeUnmount, provide, reactive, ref} from 'vue';
import AuthLoginComponent from '@/components/auth/blocks/login/login.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faUser, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(faUser);
library.add(faXmark);

export default {
  name: 'NavbarView',
  props: {
    config: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: false,
    },
    items: {
      type: Array,
      required: true,
    },
    accountUrl: {
      type: String,
      required: false,
    },
    event: {
      type: [Number, String],
      required: false,
    },
    logoUrl: {
      type: String,
      required: false,
    }
  },
  components: { AuthLoginComponent, FontAwesomeIcon },
  setup(props) {

    const config = { ...inject('config'), ...props.config };
    provide('config', config);

    const eventProvider = inject('eventProvider');
    const authProvider = inject('authProvider');

    const state = reactive({
      notification: ref(null),
      showLogin: ref(false),
      showOverlay: ref(false),
      user: computed(() => authProvider().getUser),
      showDropDown: false,
      showMobileMenu: ref(false),
      showAccountLinks: ref(false),
      hasUser: ref(false),
    });

    const initialize = async () => {
      if (props.user) {
        await loadUser(props.user);
      } else if ( authProvider().hasUserCookie ) {
        await loadUser(authProvider().getUserCookie);
      } else {
        const cookie = authProvider().loadUserCookie(config);
        if (cookie) {
          await loadUser(cookie);
        }
      }

      if (props.event && props.accountUrl) {
        if (state.user) {
          await loadEvent();
        } else {
          state.notification = 'Please login with the account used to purchase this event.';
          state.showLogin = true;
          state.showOverlay = true;
        }
      }

    };

    const logout = async () => {
      state.hasAccess = false;
      state.showLogin = true;
      state.hasUser = false;
      await authProvider().logout(config);
      await authProvider().deleteUserCookie(config);
      if (props.event && props.accountUrl) {
        state.notification = 'Please login with the account used to purchase this event.';
        state.showOverlay = true;
      }
    };

    const loadUser = async (user) => {
      state.notification = 'Loading User...';
      const { id, token } = user;
      let response = await authProvider().loadUser(id, token, config);
      await handleLoginResponse(response);
    };

    const login = async (data) => {
      state.notification = 'Validating Credentials...';
      const { username, password } = data;
      const response = await authProvider().login(username, password, config);
      await handleLoginResponse(response);
    };

    const handleLoginResponse = async (response) => {
      if (response.status === 'error' || response.success === false) {
        state.showLogin = true;
        state.notification = response.data.message || response.data.data.message || response.data.data;
      } else {
        state.showLogin = false;
        state.notification = 'Verifying User Access';
        state.hasUser = true;
        document.body.classList.remove('koc-has-overlay');
        if (props.event) {
          await loadEvent();
        } else {
          state.notification = null;
          state.showOverlay = false;
        }
      }
    };

    const loadEvent = async () => {
      let response = await eventProvider().loadEvent(props.event, config, authProvider().getUser.token);
      if (response.status === 'error' || response.success === false) {
        state.notification = 'There was an error loading the event data.';
        if (config.debug) {
          console.log('loadEvent error', props.event, response.message);
        }
      } else {
        await handleEventResponse(response);
      }

      return response;
    };

    const handleEventResponse = async (response) => {
      state.event = response.data.data;
      let firstTrack = state.event.tracks[0];
      if (!firstTrack) {
        state.notification = `A valid track ID could not be located inside this event`;
        state.showOverlay = true;
        return;
      }
      if (!firstTrack.video_key) {
        state.notification = `This account does not have access to the ${state.event.name}<br/> <a href="${state.event.checkout_url}">PURCHASE ACCESS TO THE EVENT</a>`;
        state.showOverlay = true;
        return;
      }
      state.notification = null;
      state.showOverlay = false;
    };

    initialize();

    const loginListener = async (event) => {
      if (state.user && event.detail.user.id !== state.user.id) {
        await authProvider().logout(config);
        state.hasAccess = false;
      }
      await loadUser(event.detail.user);
    }

    const logoutListener = async () => {
      if (state.hasUser) {
        await logout();
      }
    }

    document.addEventListener('koc-login', async (event) => {
      await loginListener(event);
    });

    document.addEventListener('koc-logout', async () => {
      await logoutListener();
    });


    onBeforeUnmount(() => {
      document.removeEventListener('koc-login', loginListener);
      document.removeEventListener('koc-logout', logoutListener);
    });

    return {
      state,
      loadEvent,
      loadUser,
      login,
      logout
    };
  },
  methods: {
    mouseOverAccountMenu: function() {
      this.state.showAccountLinks = true;
    },
    mouseLeaveAccountMenu: function() {
      this.state.showAccountLinks = false;
    },
    logoutMethod: function() {
      this.logout();
    },
    showLoginOverlay() {
      this.state.showLogin = true;
      this.state.showOverlay = true;
      this.state.showAccountLinks = false;
      this.state.showMobileMenu = false;
    },
  },
};
